import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile widget — обёртка для CF challenges.cloudflare.com/turnstile.
 *
 * Backend проверяет `cfTurnstileToken` через Cloudflare siteverify. Обязательность
 * определяется TURNSTILE_MODE; rollout-режим не ломает старые native-клиенты.
 * Этот компонент рендерит CF widget и вызывает `onToken(token)` когда юзер
 * прошёл (или CF Managed mode прошёл его автоматически без UI).
 *
 * Loader скрипта `challenges.cloudflare.com/turnstile/v0/api.js` грузится один
 * раз на страницу: компонент сам ставит `<script>` если ещё нет, и ждёт
 * `window.turnstile` через polling-промис. Так можно ставить виджет в N разных
 * местах (Register/ForgotPassword/Resend), но скрипт грузится только один раз.
 *
 * Site key публичный — лежит в `/var/www/.ssoenv` как `TURNSTILE_SITE_KEY`,
 * привязан в CF Dashboard к hostnames swipa.ru/karaokebogema.ru/chat56.ru.
 * Здесь хардкод — overridable через `VITE_TURNSTILE_SITE_KEY` если когда-то
 * понадобится pivot на новый ключ без передеплоя кода.
 */

const DEFAULT_SITE_KEY = '0x4AAAAAADSbYUvPyVXtT-Tx';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        opts: {
          sitekey: string;
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact' | 'flexible';
          action?: string;
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile requires browser env'));
  }
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    // Сначала проверим — может тег уже стоит (например из HMR или race условия).
    const existing = document.querySelector<HTMLScriptElement>(`script[src^="${SCRIPT_URL}"]`);
    if (existing) {
      // Подождём пока window.turnstile появится; интервал 50мс с timeout 10s.
      const start = Date.now();
      const tick = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(tick);
          resolve();
        } else if (Date.now() - start > 10_000) {
          window.clearInterval(tick);
          reject(new Error('Turnstile script timed out'));
        }
      }, 50);
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

interface CloudflareTurnstileProps {
  /** Вызывается когда CF выдал токен. Один токен живёт ~5 минут на стороне CF. */
  onToken: (token: string) => void;
  /** Опционально — реагирует на expired. По умолчанию мы просим reset() и onToken('') */
  onExpire?: () => void;
  /** Опционально — реагирует на CF error. По умолчанию onToken('') */
  onError?: () => void;
  /** Тема: по умолчанию dark, под наш surface-950 */
  theme?: 'light' | 'dark' | 'auto';
  /** Полезно знать какой action на бэке логируется (register / forgot-password / ...) */
  action?: string;
}

export function CloudflareTurnstile({
  onToken,
  onExpire,
  onError,
  theme = 'dark',
  action,
}: CloudflareTurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const siteKey =
      (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? DEFAULT_SITE_KEY;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          action,
          callback: (token) => {
            onToken(token);
          },
          'expired-callback': () => {
            if (onExpire) onExpire();
            else {
              onToken('');
              if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
            }
          },
          'error-callback': () => {
            if (onError) onError();
            else onToken('');
          },
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[Turnstile] init failed:', err);
        if (onError) onError();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Виджет мог уже сам сняться, не страшно.
        }
        widgetIdRef.current = null;
      }
    };
    // Намеренно НЕ включаем onToken/onExpire/onError в deps — мы хотим инициализировать
    // виджет ровно один раз. Если parent перерисует callback'и, всё равно будет работать
    // потому что мы дёргаем последнюю версию через замыкание useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, action]);

  return <div ref={containerRef} className="flex justify-center" />;
}
